import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ACCOUNTING_API } from '../config/api';
import { accountingApi } from '../config/apiClient';
import { useAuth } from './AuthContext';

/**
 * DueRemindersContext — vade hatırlatmalarının tek veri kaynağı.
 *
 * Ödeme planlaması yapılan alanları (kredi taksiti, kredi kartı ekstresi,
 * çek/senet) tek istekle çeker ve iki tüketiciye birlikte servis eder. Faturalar dahil değildir.
 *  - DueRemindersNotifier → sağ altta açılışta çıkan uyarı bandı
 *  - NotificationMenu (Header) → sağ üstteki bildirim zili ve sayacı
 *
 * Bu bir SİSTEM bildirimidir (kullanıcıya gönderilen push değil): içerik canlı
 * veriden türetilir, okundu/okunmadı durumu tutulmaz. Zil sayacı o an vadesi
 * geçmiş/yaklaşan kalem sayısını gösterir ve kalem kapandığında kendiliğinden düşer.
 *
 * Görünürlük: şirkette 'accounting' modülü açık olmalı ve rol admin/subadmin olmalı;
 * alt admin yalnızca izinli olduğu ekranların hatırlatmalarını görür (filtre tüketicide).
 */

export interface DueReminderTotal {
  currency: string;
  amount: number;
}

export interface DueReminderItem {
  label: string;
  dueDate: string;
  amount: number;
  currency: string;
  daysToDue: number;
  searchText?: string;
}

export interface DueReminderGroup {
  source: string;
  direction: 'PAYABLE' | 'RECEIVABLE';
  route: string;
  permissionSection: string;
  permissionItem: string;
  overdueCount: number;
  upcomingCount: number;
  overdueTotals: DueReminderTotal[];
  upcomingTotals: DueReminderTotal[];
  mostOverdueLabel?: string | null;
  mostOverdueDays: number;
  nearestLabel?: string | null;
  nearestDueDate?: string | null;
  items?: DueReminderItem[];
}

/** Kaynak bazlı metinler — backend DueReminderSource kodlarıyla birebir eşleşir. */
export const DUE_REMINDER_SOURCE_TEXTS: Record<
  string,
  { nameTr: string; nameEn: string; goTr: string; goEn: string }
> = {
  LOAN_INSTALLMENT: { nameTr: 'kredi taksidi', nameEn: 'loan installment(s)', goTr: 'Kredilere Git', goEn: 'Go to Loans' },
  CREDIT_CARD: { nameTr: 'kredi kartı ekstresi', nameEn: 'credit card statement(s)', goTr: 'Kartlara Git', goEn: 'Go to Cards' },
  CHECK_NOTE_GIVEN: { nameTr: 'verilen çek/senet', nameEn: 'issued check/note(s)', goTr: 'Çek/Senete Git', goEn: 'Go to Checks' },
  CHECK_NOTE_RECEIVED: { nameTr: 'alınan çek/senet', nameEn: 'received check/note(s)', goTr: 'Çek/Senete Git', goEn: 'Go to Checks' },
};

const EXCLUDED_REMINDER_SOURCES = new Set(['PURCHASE_INVOICE', 'SALES_INVOICE']);

export const dueReminderCurrencySymbol = (c: string) =>
  c === 'TRY' ? '₺' : c === 'USD' ? '$' : c === 'EUR' ? '€' : c;

/** "12.500,00 ₺ + 300,00 $" biçiminde para birimi bazlı toplam metni */
export const formatDueReminderTotals = (totals: DueReminderTotal[], locale: string): string =>
  (totals || [])
    .map(
      (t) =>
        `${Number(t.amount || 0).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${dueReminderCurrencySymbol(t.currency)}`
    )
    .join(' + ');

export const formatDueReminderAmount = (amount: number, currency: string, locale: string): string =>
  `${Number(amount || 0).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${dueReminderCurrencySymbol(currency)}`;

const CHECK_INTERVAL_MS = 60 * 60 * 1000; // 60 dakika
const DAYS_AHEAD = 5;

interface DueRemindersContextValue {
  /** Yetki filtresinden geçmiş, uyarılacak kalemi olan kaynaklar (gecikmişler önce). */
  groups: DueReminderGroup[];
  /** Vadesi geçmiş + yaklaşan toplam kalem sayısı (zil rozeti). */
  totalCount: number;
  /** Yalnızca vadesi geçmiş kalem sayısı. */
  overdueCount: number;
  loading: boolean;
  /** Hatırlatma sistemi bu kullanıcı için aktif mi (modül + rol kapısı). */
  enabled: boolean;
  refresh: () => Promise<void>;
}

const DueRemindersContext = createContext<DueRemindersContextValue>({
  groups: [],
  totalCount: 0,
  overdueCount: 0,
  loading: false,
  enabled: false,
  refresh: async () => {},
});

export const useDueReminders = () => useContext(DueRemindersContext);

export const DueRemindersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userDetails, isModuleEnabled, isSubAdmin, adminPanelPermissions, adminPanelLoading } = useAuth();

  const [groups, setGroups] = useState<DueReminderGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const fetchingRef = useRef(false);

  const companyId = userDetails?.companyId;
  const accountingEnabled = isModuleEnabled('accounting');
  // Finansal vade bilgisi yalnızca yönetici rollerine gösterilir. (MainLayout rol
  // koruması olmayan birkaç rotada da kullanılıyor, ör. satış ekranı raporları.)
  const isAdminRole = userDetails?.role === 'admin' || userDetails?.role === 'subadmin';
  const enabled = Boolean(companyId) && accountingEnabled && isAdminRole;

  /** Alt admin yalnızca izinli olduğu ekranların hatırlatmasını görür. */
  const isGroupAllowed = useCallback(
    (group: DueReminderGroup) => {
      if (!isSubAdmin) return true;
      const section = adminPanelPermissions?.[group.permissionSection];
      if (!section || section.enabled !== true) return false;
      return section.items?.[group.permissionItem] === true;
    },
    [isSubAdmin, adminPanelPermissions]
  );

  const refresh = useCallback(async () => {
    if (!enabled || fetchingRef.current) return;
    // Alt admin izinleri yüklenmeden filtreleme yapılamaz — bir tur bekle.
    if (isSubAdmin && adminPanelLoading) return;

    fetchingRef.current = true;
    setLoading(true);
    try {
      const res = await accountingApi.get(ACCOUNTING_API.DUE_REMINDER_SUMMARY(DAYS_AHEAD));
      const payload = res.data?.data ?? res.data;
      const incoming: DueReminderGroup[] = payload?.groups || payload?.Groups || [];

      const visible = incoming
        .filter((g) => !EXCLUDED_REMINDER_SOURCES.has(g.source || (g as { Source?: string }).Source || ''))
        .map((g) => {
          const raw = g as DueReminderGroup & { Items?: DueReminderItem[]; Source?: string };
          const rawItems = Array.isArray(g.items) ? g.items : Array.isArray(raw.Items) ? raw.Items : [];
          const items = rawItems.map((it) => {
            const anyItem = it as DueReminderItem & { SearchText?: string };
            return { ...it, searchText: it.searchText || anyItem.SearchText || '' };
          });
          return { ...g, source: g.source || raw.Source || '', items };
        })
        .filter((g) => g.overdueCount > 0 || g.upcomingCount > 0)
        .filter((g) => isGroupAllowed(g))
        .sort((a, b) => Number(b.overdueCount > 0) - Number(a.overdueCount > 0));

      const itemTotal = visible.reduce((sum, g) => sum + (g.items?.length ?? 0), 0);
      const countTotal = visible.reduce((sum, g) => sum + g.overdueCount + g.upcomingCount, 0);
      console.log('[DueReminders] ham yanıt', {
        url: ACCOUNTING_API.DUE_REMINDER_SUMMARY(DAYS_AHEAD),
        status: res.status,
        payloadKeys: payload ? Object.keys(payload) : [],
        groupCount: incoming.length,
        visibleCount: visible.length,
        badgeCount: countTotal,
        itemsInResponse: itemTotal,
      });
      visible.forEach((g) => {
        console.log('[DueReminders] grup', {
          source: g.source,
          overdueCount: g.overdueCount,
          upcomingCount: g.upcomingCount,
          itemsLength: g.items?.length ?? 0,
          keys: Object.keys(g),
          firstItem: g.items?.[0] ?? null,
        });
      });
      if (itemTotal === 0 && countTotal > 0) {
        console.warn(
          '[DueReminders] API kalem listesi (items) gondermiyor. Rozet sayisi grup ozetinden geliyor; liste her gruptan 1 satira dusuyor. gts_accounting_api deploy edilmis mi?'
        );
      }

      setGroups(visible);
    } catch (err) {
      // Hatırlatma sistemi uygulamayı asla bloklamaz
      console.warn('[DueReminders] Vade kontrolü yapılamadı:', err);
    } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  }, [enabled, isSubAdmin, adminPanelLoading, isGroupAllowed]);

  useEffect(() => {
    if (!enabled) {
      setGroups([]);
      return;
    }
    refresh();
    const interval = setInterval(refresh, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled, refresh]);

  const value = useMemo<DueRemindersContextValue>(() => {
    const overdueCount = groups.reduce((sum, g) => sum + g.overdueCount, 0);
    const totalCount = groups.reduce((sum, g) => sum + g.overdueCount + g.upcomingCount, 0);
    return { groups, totalCount, overdueCount, loading, enabled, refresh };
  }, [groups, loading, enabled, refresh]);

  return <DueRemindersContext.Provider value={value}>{children}</DueRemindersContext.Provider>;
};
